import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, SparkPrepLogo } from "@/components/Logo";

export default function Nav({ dark = true }) {
  const { user, logout } = useAuth();
  const base = dark
    ? "border-b border-neutral-900 bg-[#0D0D0D]/90 backdrop-blur-xl"
    : "border-b border-neutral-200 bg-[#F7F7F5]/90 backdrop-blur-xl";
  const linkCls = dark ? "text-neutral-400 hover:text-white transition-colors" : "text-neutral-600 hover:text-neutral-900 transition-colors";
  return (
    <nav className={`sticky top-0 z-50 ${base}`} data-testid="top-nav">
      <div className="max-w-7xl mx-auto relative flex flex-wrap items-center justify-between gap-y-1 px-6 py-4 overflow-visible">
        <Link to="/" data-testid="nav-logo">
          <Logo dark={dark} compact />
        </Link>
        {/* SparkPrep's own main logo -- the brand being promoted, so it gets
            the dead-center spot at a larger size than the Legenddary mark.
            Matches the size of the SparkPrep logo in the landing page's
            bottom CTA (height 140). It's taller than the nav bar itself and
            deliberately overflows below it (transparent PNG, so this reads
            as the logo "breaking out" of a slim bar rather than forcing the
            whole sticky nav to be that tall all the time). Anchored to the
            bar's top edge rather than vertically centered, since the nav
            sticks to the very top of the viewport when scrolling --
            centering it would let the top half get clipped by the browser
            edge with nowhere to overflow into. Desktop only: at narrower
            widths this absolute-centered placement collides with the nav
            links (Specs/Pricing/Audit/Login), which don't collapse behind a
            hamburger menu -- see the mobile version below instead. */}
        <Link
          to="/"
          className="hidden md:block absolute left-1/2 top-2 -translate-x-1/2 z-10"
          data-testid="nav-sparkprep-logo"
        >
          <SparkPrepLogo height={140} />
        </Link>
        <div className="flex items-center gap-5 text-sm">
          {user?.beta_active && (
            <Link to="/beta/feedback" className="font-mono-spec text-[10px] tracking-widest uppercase text-black bg-[#D4AF37] px-2.5 py-1.5 hover:brightness-110 btn-industrial" data-testid="nav-beta-badge">
              BETA · Sign Off
            </Link>
          )}
          {user?.is_admin && (
            <Link to="/admin/beta" className={`${linkCls} font-mono-spec text-[10px] tracking-widest uppercase border ${dark ? "border-neutral-700" : "border-neutral-300"} px-2 py-1`} data-testid="nav-admin">
              Admin
            </Link>
          )}
          <Link to="/audit" className="font-mono-spec text-[10px] tracking-widest uppercase text-white bg-[#FF6A00] px-2.5 py-1.5 hover:brightness-110 btn-industrial" data-testid="nav-audit">
            99¢ Audit
          </Link>
          <Link to="/specs" className={linkCls} data-testid="nav-specs">Specs</Link>
          <Link to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</Link>
          {user ? (
            <>
              <Link to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/account" className={linkCls} data-testid="nav-account">Account</Link>
              <button onClick={logout} className={`${linkCls} font-mono-spec text-xs tracking-widest uppercase`} data-testid="nav-logout">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className={linkCls} data-testid="nav-login">Log In</Link>
              <Link to="/register" className="px-4 py-2 btn-gold font-mono-spec text-xs tracking-widest uppercase btn-industrial" data-testid="nav-signup">
                Start Free
              </Link>
            </>
          )}
        </div>
        {/* Mobile version of the SparkPrep logo above: the desktop one is
            absolutely centered and overlaps the nav links at narrower
            widths (this nav has no hamburger/collapsed-menu state), so
            below md this renders as an ordinary flex child instead --
            `w-full` forces the flex-wrap container to break here, giving it
            its own centered row under the links rather than sitting on top
            of them. Without this, SparkPrep's own logo was either invisible
            under 768px (the original bug) or overlapping nav text (the
            first attempt at fixing it). */}
        <Link to="/" className="md:hidden w-full flex justify-center pt-1" data-testid="nav-sparkprep-logo-mobile">
          <SparkPrepLogo height={64} />
        </Link>
      </div>
    </nav>
  );
}
